import gql from 'graphql-tag';
import { useQuery as defaultUseQuery } from '@apollo/react-hooks';
import {
  ApolloInjection,
  PaymentSource,
  SupportedPaymentMethod,
} from '@zapier/payment/src/types';

type Input = {
  accountId: number | undefined;
  skip?: boolean;
} & ApolloInjection;

type Output = {
  error: string | undefined | null;
  forceSourceEdit: boolean;
  isLoading: boolean;
  paymentSources: Array<PaymentSource>;
  primarySource: PaymentSource | undefined | null;
  supportedPaymentMethods: Array<SupportedPaymentMethod>;
};

export const paymentMethodsQuery = gql`
  query paymentMethodQuery($id: Int!) {
    account(id: $id) {
      id
      paymentSources {
        details {
          ... on PaymentSourceBank {
            accountHolderName
            accountType
            addressCity
            addressCountry
            addressState
            addressZip
            bankName
            last4
            ownershipType
            routingNumber
            verified
          }

          ... on PaymentSourceCard {
            addressCity
            addressCountry
            addressState
            addressZip
            brand
            expiry
            last4
            name
          }

          ... on PaymentSourcePaypal {
            email
          }
        }
        id
        method
        submethod
      }
      supportedPaymentMethodsv2 {
        method
        paymentProcessor
      }
    }
  }
`;

const usePaymentSource = ({
  accountId,
  skip = false,
  useQuery = defaultUseQuery,
}: Input): Output => {
  const queryResults = useQuery(paymentMethodsQuery, {
    skip: skip || !accountId,
    variables: {
      id: accountId,
    },
  });

  const paymentSources = queryResults?.data?.account?.paymentSources ?? [];
  const primarySource = paymentSources[0];

  /**
   * We only forced editing a payment source if:
   *   a) the user was using a credit card and
   *   b) they didn't have at least a country declared in their billing address
   * However, as of Feb 15, 2021, we don't require the billing address's country when paying with a card.
   *
   * We'll keep the `forceSourceEdit` around for a) backwards compatibility and b) because
   * we will likely revert this change in the future to re-require address country when
   * using a card. See JIRA ticket below.
   *
   * @see https://zapierorg.atlassian.net/browse/BILL-827
   */
  const forceSourceEdit = false;

  return {
    error: queryResults.error
      ? 'There was an issue loading your payment sources. Please try again'
      : null,
    forceSourceEdit,
    isLoading: queryResults.loading,
    paymentSources,
    primarySource,
    supportedPaymentMethods:
      queryResults?.data?.account?.supportedPaymentMethodsv2 ?? [],
  };
};

export default usePaymentSource;
